import { Link } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { useCredentials, findCredentialByMarketplaceCode } from "@/lib/hooks/useCredentials";
import type { AffiliateCredentialPublic, SupportedMarketplaceCode } from "@/lib/types";
import { AmazonCredentialSection } from "@/components/credentials/AmazonCredentialSection";
import { MagaluCredentialSection } from "@/components/credentials/MagaluCredentialSection";
import { RakutenCredentialSection } from "@/components/credentials/RakutenCredentialSection";
import { SheinCredentialSection } from "@/components/credentials/SheinCredentialSection";
import { MercadoLivreCredentialSection } from "@/components/MercadoLivreCredentialSection";
import { AliExpressCredentialSection } from "@/components/AliExpressCredentialSection";
import { ShopeeCredentialSection } from "@/components/ShopeeCredentialSection";
import { marketplaceLabel } from "@/lib/marketplace";

type Section = (props: { credential: AffiliateCredentialPublic | undefined; loading: boolean }) => JSX.Element;

/** Os mesmos cards de credencial de Minha conta, por loja escolhida. */
const SECTIONS: Readonly<Record<string, Section>> = {
  amazon: ({ credential }) => <AmazonCredentialSection credential={credential} />,
  mercadolivre: ({ credential, loading }) => (
    <MercadoLivreCredentialSection credential={credential} loadingCredentials={loading} />
  ),
  aliexpress: ({ credential, loading }) => (
    <AliExpressCredentialSection credential={credential} loadingCredentials={loading} sectionId="onboarding-aliexpress" />
  ),
  shopee: ({ credential, loading }) => (
    <ShopeeCredentialSection credential={credential} loadingCredentials={loading} sectionId="onboarding-shopee" />
  ),
  rakuten: ({ credential }) => <RakutenCredentialSection credential={credential} />,
  magalu: ({ credential }) => <MagaluCredentialSection credential={credential} />,
  shein: ({ credential }) => <SheinCredentialSection credential={credential} />,
};

/** Passo 4: credenciais das lojas marcadas no passo anterior. */
export function StepCredenciais({ platforms }: { platforms: readonly string[] }): JSX.Element {
  const { data: credentials = [], isLoading } = useCredentials();

  if (platforms.length === 0) {
    return (
      <p className="rounded-lg border border-border bg-[hsl(var(--surface-raised)/0.4)] p-4 text-sm text-muted-foreground">
        Você não marcou nenhuma loja. Volte um passo para escolher, ou pule e cadastre depois em Minha conta.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {platforms.map((code) => {
        const Section = SECTIONS[code];
        if (!Section) {
          return (
            <p key={code} className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
              A credencial da {marketplaceLabel(code)} fica em{" "}
              <Link to="/app/account" className="inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline">
                Minha conta <ExternalLink size={12} aria-hidden="true" />
              </Link>
              .
            </p>
          );
        }
        const credential = findCredentialByMarketplaceCode(credentials, code as SupportedMarketplaceCode);
        return <Section key={code} credential={credential} loading={isLoading} />;
      })}
    </div>
  );
}
